import React from 'react';
import type { Theme } from '@material-ui/core/styles/createMuiTheme';
import useMediaQuery from '@material-ui/core/useMediaQuery';
import Skeleton from '@material-ui/lab/Skeleton';
import { makeStyles } from '@material-ui/styles';
import cs from 'classnames';
import { Document } from 'react-pdf';
import { MD_MEDIA_QUERY, SKELETON_HEIGHT, SKELETON_WIDTH } from '../../common/constants';
import pdfCache from '../../lib/pdfCache';
import type { IBlockState } from '../../store/reducers/blocks';
import type { IEnvelopeState } from '../../store/reducers/envelope';
import type { IGroupState } from '../../store/reducers/groups';
import type { IDocument, IBlock, ISignatureSettings } from '../../store/types';
import { boxShadows } from '../../theme';
import { SignerAd } from '../SignerAd';
import { PdfBlocks } from './PdfBlocks';
import { PdfPage } from './PdfPage';
import { PdfPageSkeleton } from './PdfPageSkeleton';
import { shouldRenderThisPage } from './should-render-this-page';

const useStyles = makeStyles<Theme, { isMobile: boolean }>(
  (theme: Theme) => ({
    container: {
      position: 'fixed',
      top: (p) => (p.isMobile ? theme.spacing(7) : theme.spacing(14)),
      bottom: 80,
      left: 0,
      right: 0,
      overflowY: 'auto',
    },
    document: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
    },
    documentPadding: {
      padding: '0 8px',
    },
    skeleton: {
      boxShadow: boxShadows.main,
      backgroundColor: 'rgba(255,255,255,0.25)',
      margin: '48px auto 0',
    },
    hidden: {
      display: 'none',
    },
  }),
  { name: 'PdfViewer' }
);

interface PdfViewerProps {
  documents: IDocument[];
  zoom: number;
  clickSignature: (documentId: string, pageNumber: number, blockId: string, tabIndex?: number) => void;
  updateBlock: (documentId: string, pageNumber: number, blockId: string, blockUpdate: any) => void;
  blocks: IBlockState;
  groups?: IGroupState;
  envelope: IEnvelopeState;
  signingExperienceBlocks: IBlock[];
  allBlocksComplete: boolean;
  currentSelectedBlockIndex: number | null;
  signingExperienceComplete: boolean;
  scrollToNextBlock: () => void;
  tabToBlock: (nextIndex: number) => void;
  onPageLoad: (page: any, fullPage: number) => void;
  pageDimensions: Record<number, { width?: number; height?: number }>;
  pageScale: { [pageNumber: number]: number };
  setPageScale: (fullPage: number, pageScale: number) => void;
  clearAllPageScales: () => void;
  signatureSettings?: ISignatureSettings;
  setBlocksInGroupToComplete: (blocks: IBlock[]) => void;
  setCurrentDocument: (documentId: string, documentName: string) => void;
}

export function PdfViewer(props: PdfViewerProps) {
  const {
    envelope,
    blocks,
    documents,
    signingExperienceBlocks,
    currentSelectedBlockIndex,
    signingExperienceComplete,
    groups,
    setCurrentDocument,
    zoom,
    onPageLoad,
    clearAllPageScales,
  } = props;

  const isMobile = useMediaQuery(MD_MEDIA_QUERY);
  const classes = useStyles({ isMobile });

  const [firstPageLoaded, setFirstPageLoaded] = React.useState(false);
  const [isAutoScrolling, setIsAutoScrolling] = React.useState(false);
  const [visiblePage, setVisiblePage] = React.useState(1); // 1-based index

  // If the user resizes the browser clear out all the cached page scales
  React.useEffect(() => {
    let resizeTimeout: NodeJS.Timeout;
    function resizeHandler() {
      clearTimeout(resizeTimeout);
      resizeTimeout = setTimeout(() => clearAllPageScales(), 250);
    }
    window.addEventListener('resize', resizeHandler);
    return () => window.removeEventListener('resize', resizeHandler);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function scrollToBlock(blockElement: HTMLDivElement, crossDocPageIndex: number) {
    setIsAutoScrolling(true);
    blockElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
    setIsAutoScrolling(false);
    setVisiblePage(crossDocPageIndex);
    blockElement.focus({ preventScroll: true });
  }

  function handleSetVisiblePage(args: { crossDocPageIndex: number; documentId: string; documentName: string }) {
    setCurrentDocument(args.documentId, args.documentName);
    if (!isAutoScrolling) setVisiblePage(args.crossDocPageIndex);
  }

  function getGroups(documentId: string, pageNumber: number) {
    return groups?.[documentId]?.[pageNumber];
  }

  function handlePageLoad(page: any, crossDocPageIndex: number) {
    if (crossDocPageIndex === 1) setFirstPageLoaded(true);
    onPageLoad(page, crossDocPageIndex);
  }

  let crossDocPageIndex = 0; // 1-based index, will be pre-incremented
  return (
    <div className={`PdfViewer ${classes.container} ds--pdf-container`}>
      <SignerAd />
      {!firstPageLoaded && (
        <Skeleton
          className={classes.skeleton}
          variant="rect"
          animation="wave"
          height={SKELETON_HEIGHT * zoom}
          width={SKELETON_WIDTH * zoom}
        />
      )}
      {documents.map((_doc: IDocument, documentIndex: number) => {
        const document = documents.find(({ order }) => order === documentIndex + 1);
        if (!document) return null;

        return (
          <Document
            key={document.documentId}
            className={cs(classes.document, !firstPageLoaded && classes.hidden, isMobile && classes.documentPadding)}
            file={pdfCache[document.documentId]}
            onLoadError={(error: any) => console.error(error)}
            externalLinkTarget="_blank"
            loading=""
          >
            {[...Array(document.numberOfPages)].map((_, pageNumber) => {
              crossDocPageIndex++;

              const thisPageBlocks = blocks[document.documentId]?.[pageNumber] || {};
              let selectedBlock: IBlock | undefined = undefined;
              if (currentSelectedBlockIndex !== null) {
                if (
                  signingExperienceBlocks[currentSelectedBlockIndex].documentId === document.documentId &&
                  signingExperienceBlocks[currentSelectedBlockIndex].pageNumber === pageNumber
                ) {
                  let temps = signingExperienceBlocks[currentSelectedBlockIndex];
                  selectedBlock = thisPageBlocks[temps.blockId];
                  // Feels like we should just be able to do this?
                  // selectedBlock = signingExperienceBlocks[currentSelectedBlockIndex];
                }
              }

              const pageDimensions = document.pages
                ? document.pages[pageNumber]
                : props.pageDimensions[crossDocPageIndex];

              const doRenderThisPage = shouldRenderThisPage({
                currentlyVisiblePageIndex: visiblePage,
                crossDocPageIndex,
              });

              return (
                <PdfPageSkeleton
                  key={`${document.documentId}-${pageNumber}`}
                  onVisible={(index) =>
                    handleSetVisiblePage({
                      crossDocPageIndex: index,
                      documentId: document.documentId,
                      documentName: document.fileName,
                    })
                  }
                  zoom={zoom}
                  pageDimensions={pageDimensions || { height: NaN, width: NaN }}
                  crossDocPageIndex={crossDocPageIndex}
                  documentPages={document.numberOfPages}
                  documentName={document.fileName}
                  documentIndex={documentIndex}
                  pageNumber={pageNumber}
                  totalDocuments={documents.length}
                  totalPages={document.numberOfPages}
                >
                  {doRenderThisPage ? (
                    <PdfPage
                      key={pageNumber}
                      documentIndex={documentIndex}
                      documentId={document.documentId}
                      envelope={envelope}
                      blocks={thisPageBlocks}
                      scrollToNextBlock={props.scrollToNextBlock}
                      allBlocksComplete={props.allBlocksComplete}
                      currentSelectedBlockIndex={currentSelectedBlockIndex}
                      signingExperienceBlocks={signingExperienceBlocks}
                      crossDocPageIndex={crossDocPageIndex}
                      pageNumber={pageNumber}
                      zoom={zoom}
                      onLoadSuccess={handlePageLoad}
                      pageDimensions={pageDimensions || { height: NaN, width: NaN }}
                      pageScale={props.pageScale[crossDocPageIndex]}
                      setPageScale={props.setPageScale}
                      isMobile={isMobile}
                      selectedBlock={selectedBlock}
                    />
                  ) : null}
                  <PdfBlocks
                    blocks={thisPageBlocks}
                    groups={getGroups(document.documentId, pageNumber)}
                    envelope={envelope}
                    signingExperienceBlocks={signingExperienceBlocks}
                    currentSelectedBlockIndex={currentSelectedBlockIndex}
                    signingExperienceComplete={signingExperienceComplete}
                    zoom={zoom}
                    pageScale={props.pageScale[crossDocPageIndex]}
                    setBlocksInGroupToComplete={props.setBlocksInGroupToComplete}
                    fullPage={crossDocPageIndex}
                    scrollToBlock={scrollToBlock}
                    pageDimensions={pageDimensions || { height: NaN, width: NaN }}
                    clickSignature={props.clickSignature.bind(null, document.documentId, pageNumber)}
                    updateBlock={props.updateBlock.bind(null, document.documentId, pageNumber)}
                    tabToBlock={props.tabToBlock}
                    signatureSettings={props.signatureSettings}
                    selectedBlock={selectedBlock}
                  />
                </PdfPageSkeleton>
              );
            })}
          </Document>
        );
      })}
    </div>
  );
}
